import _slicedToArray from "/home/runner/work/experian-public-site-tng/experian-public-site-tng/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import "core-js/modules/es.array.sort.js";
function _createForOfIteratorHelper(r, e) { var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (!t) { if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e && r && "number" == typeof r.length) { t && (r = t); var _n = 0, F = function F() {}; return { s: F, n: function n() { return _n >= r.length ? { done: !0 } : { done: !1, value: r[_n++] }; }, e: function e(r) { throw r; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var o, a = !0, u = !1; return { s: function s() { t = t.call(r); }, n: function n() { var r = t.next(); return a = r.done, r; }, e: function e(r) { u = !0, o = r; }, f: function f() { try { a || null == t["return"] || t["return"](); } finally { if (u) throw o; } } }; }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
import { elapsed, ExperimentalFeature, isExperimentalFeatureEnabled, noop, ONE_MINUTE } from '@datadog/browser-core';
import { createPerformanceObservable, RumPerformanceEntryType, supportPerformanceTimingEvent } from '../../../browser/performanceObservable';
import { ViewLoadingType } from '../../../rawRumEvent.types';
import { getSelectorFromElement } from '../../getSelectorFromElement';
import { isElementNode } from '../../../browser/htmlDomUtils';
import { getInteractionSelector } from '../../action/interactionSelectorCache';
import { getInteractionCount, initInteractionCountPolyfill } from './interactionCountPolyfill';
// Arbitrary value to prevent unnecessary memory usage on views with lots of interactions.
var MAX_INTERACTION_ENTRIES = 10;
// Arbitrary value to cap INP outliers
export var MAX_INP_VALUE = 1 * ONE_MINUTE;
// Event Timing API rounds duration values to the nearest 8 ms
var RENDER_TIME_GROUPING_THRESHOLD = 8;
/**
 * Track the interaction to next paint (INP).
 * To avoid outliers, return the p98 worst interaction of the view.
 * Documentation: https://web.dev/inp/
 * Reference implementation: https://github.com/GoogleChrome/web-vitals/blob/main/src/onINP.ts
 */
export function trackInteractionToNextPaint(configuration, viewStart, viewLoadingType) {
  if (!isInteractionToNextPaintSupported()) {
    return {
      getInteractionToNextPaint: function getInteractionToNextPaint() {
        return undefined;
      },
      setViewEnd: noop,
      stop: noop
    };
  }
  var viewEnd = Infinity;
  var currentInp;
  var _trackViewInteraction = trackViewInteractionCount(viewLoadingType),
    getViewInteractionCount = _trackViewInteraction.getViewInteractionCount,
    stopViewInteractionCount = _trackViewInteraction.stopViewInteractionCount;
  var longestInteractions = trackLongestInteractions(getViewInteractionCount);
  var subPartsTracker = isExperimentalFeatureEnabled(ExperimentalFeature.INP_SUBPARTS) ? createSubPartsTracker(longestInteractions) : null;
  var firstInputSubscription = createPerformanceObservable(configuration, {
    type: RumPerformanceEntryType.FIRST_INPUT,
    buffered: true
  }).subscribe(handleEntries);
  var eventSubscription = createPerformanceObservable(configuration, {
    type: RumPerformanceEntryType.EVENT,
    // durationThreshold only impact PerformanceEventTiming entries used for INP computation which requires a threshold at 40 (default is 104ms)
    // cf: https://github.com/GoogleChrome/web-vitals/blob/3806160ffbc93c3c4abf210a167b81228172b31c/src/onINP.ts#L202-L210
    durationThreshold: 40,
    buffered: true
  }).subscribe(handleEntries);
  function handleEntries(entries) {
    var _iterator = _createForOfIteratorHelper(entries),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var entry = _step.value;
        if (entry.interactionId &&
        // Check the entry start time is inside the view bounds because some view interactions can be reported after the view end (if long duration).
        entry.startTime >= viewStart && entry.startTime <= viewEnd) {
          longestInteractions.process(entry);
          subPartsTracker === null || subPartsTracker === void 0 ? void 0 : subPartsTracker.process(entry);
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    subPartsTracker === null || subPartsTracker === void 0 ? void 0 : subPartsTracker.pruneUntracked();
    var candidate = longestInteractions.estimateP98Interaction();
    if (candidate) {
      updateCurrentInp(candidate);
    }
  }
  function updateCurrentInp(candidate) {
    var newStartTime = elapsed(viewStart, candidate.startTime);
    // startTime catches identity changes when the p98 switches to a different interaction with the same duration,
    // ensuring targetSelector and subParts always describe the same interaction.
    if (!currentInp || candidate.duration !== currentInp.duration || newStartTime !== currentInp.startTime) {
      var targetSelector = getInteractionSelector(candidate.startTime);
      if (!targetSelector && candidate.target && isElementNode(candidate.target)) {
        targetSelector = getSelectorFromElement(candidate.target, configuration.actionNameAttribute);
      }
      currentInp = {
        duration: candidate.duration,
        startTime: newStartTime,
        targetSelector: targetSelector
      };
    }
    // Recomputed on every batch: the group for the p98 interaction may have been updated
    // with new min/max timing even when the p98 identity (duration, startTime) is unchanged.
    if (subPartsTracker) {
      currentInp.subParts = subPartsTracker.computeSubParts(candidate, sanitizeInpValue(currentInp.duration));
    }
  }
  return {
    getInteractionToNextPaint: function getInteractionToNextPaint() {
      // If no INP duration where captured because of the performanceObserver 40ms threshold
      // but the view interaction count > 0 then report 0
      if (currentInp) {
        return {
          value: sanitizeInpValue(currentInp.duration),
          targetSelector: currentInp.targetSelector,
          time: currentInp.startTime,
          subParts: currentInp.subParts
        };
      } else if (getViewInteractionCount()) {
        return {
          value: 0
        };
      }
    },
    setViewEnd: function setViewEnd(viewEndTime) {
      viewEnd = viewEndTime;
      stopViewInteractionCount();
    },
    stop: function stop() {
      eventSubscription.unsubscribe();
      firstInputSubscription.unsubscribe();
      subPartsTracker === null || subPartsTracker === void 0 ? void 0 : subPartsTracker.stop();
    }
  };
}
/**
 * Maintains a bounded list of the slowest interactions seen so far, used to estimate the p98
 * interaction duration without keeping every entry in memory.
 */
function trackLongestInteractions(getViewInteractionCount) {
  var longestInteractions = [];
  function sortAndTrimLongestInteractions() {
    longestInteractions.sort(function (a, b) {
      return b.duration - a.duration;
    }).splice(MAX_INTERACTION_ENTRIES);
  }
  return {
    /**
     * Process the performance entry:
     * - if its duration is long enough, add the performance entry to the list of worst interactions
     * - if an entry with the same interaction id exists and its duration is lower than the new one, then replace it in the list of worst interactions
     */
    process: function process(entry) {
      var interactionIndex = longestInteractions.findIndex(function (interaction) {
        return entry.interactionId === interaction.interactionId;
      });
      var minLongestInteraction = longestInteractions[longestInteractions.length - 1];
      if (interactionIndex !== -1) {
        if (entry.duration > longestInteractions[interactionIndex].duration) {
          longestInteractions[interactionIndex] = entry;
          sortAndTrimLongestInteractions();
        }
      } else if (longestInteractions.length < MAX_INTERACTION_ENTRIES || entry.duration > minLongestInteraction.duration) {
        longestInteractions.push(entry);
        sortAndTrimLongestInteractions();
      }
    },
    /**
     * Compute the p98 longest interaction.
     * For better performance the computation is based on 10 longest interactions and the interaction count of the current view.
     */
    estimateP98Interaction: function estimateP98Interaction() {
      var interactionIndex = Math.min(longestInteractions.length - 1, Math.floor(getViewInteractionCount() / 50));
      return longestInteractions[interactionIndex];
    },
    isTracked: function isTracked(interactionId) {
      return longestInteractions.some(function (i) {
        return i.interactionId === interactionId;
      });
    }
  };
}
/**
 * Tracks the number of interactions that occurred during the current view. Freezes the count
 * when the view ends so that the p98 estimate remains stable after `setViewEnd` is called.
 */
export function trackViewInteractionCount(viewLoadingType) {
  initInteractionCountPolyfill();
  var previousInteractionCount = viewLoadingType === ViewLoadingType.INITIAL_LOAD ? 0 : getInteractionCount();
  var state = {
    stopped: false
  };
  function computeViewInteractionCount() {
    return getInteractionCount() - previousInteractionCount;
  }
  return {
    getViewInteractionCount: function getViewInteractionCount() {
      if (state.stopped) {
        return state.interactionCount;
      }
      return computeViewInteractionCount();
    },
    stopViewInteractionCount: function stopViewInteractionCount() {
      state = {
        stopped: true,
        interactionCount: computeViewInteractionCount()
      };
    }
  };
}
/**
 * Groups performance entries by interaction and render time to compute INP subparts
 * (input delay, processing duration, presentation delay). Entries sharing the same
 * interactionId, or whose render time falls within the 8 ms Event Timing rounding window,
 * are merged into a single group so that subparts always sum to the reported INP duration.
 */
function createSubPartsTracker(longestInteractions) {
  var groupsByInteractionId = new Map();
  function updateGroupWithEntry(group, entry) {
    group.startTime = Math.min(entry.startTime, group.startTime);
    // For each group, we keep the biggest interval possible between processingStart and processingEnd
    group.processingStart = Math.min(entry.processingStart, group.processingStart);
    group.processingEnd = Math.max(entry.processingEnd, group.processingEnd);
  }
  return {
    process: function process(entry) {
      if (entry.interactionId === undefined || !entry.processingStart || !entry.processingEnd) {
        return;
      }
      var renderTime = entry.startTime + entry.duration;
      var existingGroup = groupsByInteractionId.get(entry.interactionId);
      if (existingGroup) {
        // Update existing group with MIN/MAX values (keep original referenceRenderTime)
        updateGroupWithEntry(existingGroup, entry);
        return;
      }
      // Try to find a group within 8ms window to merge with (different interactionId, same frame)
      var _iterator2 = _createForOfIteratorHelper(groupsByInteractionId.entries()),
        _step2;
      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var _step2$value = _slicedToArray(_step2.value, 2),
            group = _step2$value[1];
          if (Math.abs(renderTime - group.referenceRenderTime) <= RENDER_TIME_GROUPING_THRESHOLD) {
            updateGroupWithEntry(group, entry);
            // Also store under this entry's interactionId for easy lookup
            groupsByInteractionId.set(entry.interactionId, group);
            return;
          }
        }
        // Create new group
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }
      groupsByInteractionId.set(entry.interactionId, {
        startTime: entry.startTime,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        referenceRenderTime: renderTime
      });
    },
    // Prune after all entries are grouped: groups not in longestInteractions can never affect p98 subparts.
    // Keeps groupsByInteractionId capped at MAX_INTERACTION_ENTRIES
    pruneUntracked: function pruneUntracked() {
      var _iterator3 = _createForOfIteratorHelper(groupsByInteractionId),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var _step3$value = _slicedToArray(_step3.value, 1),
            interactionId = _step3$value[0];
          if (!longestInteractions.isTracked(interactionId)) {
            groupsByInteractionId["delete"](interactionId);
          }
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
    },
    computeSubParts: function computeSubParts(entry, inpDuration) {
      if (!entry.processingStart || !entry.processingEnd || entry.interactionId === undefined) {
        return undefined;
      }
      var group = groupsByInteractionId.get(entry.interactionId);
      // Shouldn't happen since entries are grouped before p98 calculation.
      if (!group) {
        return undefined;
      }
      // Use group.startTime consistently to ensure subparts sum to inpDuration
      // Math.max prevents nextPaintTime from being before processingStart (Chrome implementation)
      var nextPaintTime = Math.max(group.startTime + inpDuration, group.processingStart);
      // Clamp processingEnd to not exceed nextPaintTime
      var processingEnd = Math.min(group.processingEnd, nextPaintTime);
      return {
        inputDelay: elapsed(group.startTime, group.processingStart),
        processingDuration: elapsed(group.processingStart, processingEnd),
        presentationDelay: elapsed(processingEnd, nextPaintTime)
      };
    },
    stop: function stop() {
      groupsByInteractionId.clear();
    }
  };
}
export function isInteractionToNextPaintSupported() {
  return supportPerformanceTimingEvent(RumPerformanceEntryType.EVENT) && window.PerformanceEventTiming && 'interactionId' in PerformanceEventTiming.prototype;
}
function sanitizeInpValue(inpValue) {
  return Math.min(inpValue, MAX_INP_VALUE);
}